import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, LogIn, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import PWAInstallPrompt from '../../components/employee-portal/PWAInstallPrompt';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PortalLogin = () => {
  const [empCode, setEmpCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/employee-portal/maintenance-status`).then((r) => setPaused(!!r.data.paused)).catch(() => {});
  }, []);

  useEffect(() => {
    const existingToken = localStorage.getItem('portal_token');
    if (existingToken) navigate('/employee-portal');
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/employee-portal/login`, {
        emp_code: empCode.trim(),
        password: password
      });
      if (res.data.must_change_password) {
        setMustChangePassword(true);
        setToken(res.data.access_token);
        setCurrentPassword(password);
        localStorage.setItem('portal_token', res.data.access_token);
        localStorage.setItem('portal_employee', JSON.stringify(res.data.employee));
      } else {
        localStorage.setItem('portal_token', res.data.access_token);
        localStorage.setItem('portal_employee', JSON.stringify(res.data.employee));
        navigate('/employee-portal');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/employee-portal/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      navigate('/employee-portal');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo/Branding */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
            <User className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Vynce Employee Portal</h1>
          <p className="text-sm text-gray-500 mt-1">Access your HR information</p>
        </div>

        {paused && (
          <div className="mb-4 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm" data-testid="portal-maintenance-banner">
            <Wrench className="h-5 w-5 flex-shrink-0 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Portal paused for maintenance</p>
              <p className="mt-0.5 text-xs text-amber-800">We're making some updates to the Employee Portal. Please check back a little later — thank you for your patience. For anything urgent, contact HR.</p>
            </div>
          </div>
        )}

        <Card className="shadow-lg border-0" data-testid="portal-login-card">
          <CardContent className="p-6">
            {!mustChangePassword ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Employee Code</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={empCode}
                      onChange={(e) => setEmpCode(e.target.value.toUpperCase())}
                      placeholder="e.g. EMP0010"
                      className="pl-10"
                      data-testid="portal-emp-code-input"
                      autoComplete="username"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="pl-10"
                      data-testid="portal-password-input"
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-500 text-center" data-testid="portal-login-error">{error}</p>
                )}

                <Button type="submit" className="w-full" disabled={loading || !empCode} data-testid="portal-login-btn">
                  {loading ? 'Signing in...' : <><LogIn className="h-4 w-4 mr-2" /> Sign In</>}
                </Button>

                <p className="text-xs text-gray-400 text-center mt-3">
                  First login? Use your Employee Code as password
                </p>
              </form>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="text-center mb-2">
                  <Lock className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-sm">Change Your Password</h3>
                  <p className="text-xs text-gray-500">Please set a new password to continue</p>
                </div>
                <div>
                  <Label className="text-sm">New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="mt-1"
                    data-testid="portal-new-password"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <Label className="text-sm">Confirm Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="mt-1"
                    data-testid="portal-confirm-password"
                    autoComplete="new-password"
                  />
                </div>

                {error && <p className="text-sm text-red-500 text-center">{error}</p>}

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Saving...' : 'Set Password & Continue'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
      <PWAInstallPrompt />
    </div>
  );
};

export default PortalLogin;
